import { Quaternion, Vector3 } from 'three'

const _wBody = new Vector3()
const _tBody = new Vector3()
const _Iw = new Vector3()
const _gyro = new Vector3()
const _dw = new Vector3()
const _qInv = new Quaternion()
const _spin = new Quaternion()
const _angImpulse = new Vector3()
const _dOmega = new Vector3()
const _rxn = new Vector3()
const _tmp = new Vector3()

/**
 * One 6-DOF rigid body, semi-implicit Euler (DESIGN.md §2).
 *
 * `omega` is kept in **world** frame because everything that reads it
 * (rendering, damping, auto-level) wants world; the angular update itself
 * drops into the body frame where the inertia tensor is diagonal, which
 * avoids rebuilding and inverting `I_world = R·I_body·Rᵀ` every step.
 */
export class RigidBody {
  readonly position = new Vector3()
  readonly velocity = new Vector3()
  readonly orientation = new Quaternion()
  /** rad/s, world frame */
  readonly omega = new Vector3()

  mass = 80
  /** Principal moments about the body axes, kg·m². */
  readonly inertia = new Vector3(20, 0.9, 20)

  /** Accumulators, cleared by `integrate`. */
  readonly force = new Vector3()
  readonly torque = new Vector3()

  addForce(f: Vector3): void {
    this.force.add(f)
  }

  /** Apply `f` at world offset `r` from the CoM — the whole game, really. */
  addForceAtOffset(f: Vector3, r: Vector3): void {
    this.force.add(f)
    this.torque.x += r.y * f.z - r.z * f.y
    this.torque.y += r.z * f.x - r.x * f.z
    this.torque.z += r.x * f.y - r.y * f.x
  }

  addTorque(t: Vector3): void {
    this.torque.add(t)
  }

  /** Velocity of the material point at world offset `r` from the CoM. */
  pointVelocity(r: Vector3, out: Vector3): Vector3 {
    return out.copy(this.omega).cross(r).add(this.velocity)
  }

  /** `out = I_world⁻¹ · v`, via the body frame where I is diagonal. */
  applyInverseInertia(v: Vector3, out: Vector3): Vector3 {
    _qInv.copy(this.orientation).invert()
    out.copy(v).applyQuaternion(_qInv).divide(this.inertia).applyQuaternion(this.orientation)
    return out
  }

  applyImpulse(impulse: Vector3, r: Vector3): void {
    this.velocity.addScaledVector(impulse, 1 / this.mass)
    _angImpulse.copy(r).cross(impulse)
    this.applyInverseInertia(_angImpulse, _dOmega)
    this.omega.add(_dOmega)
  }

  /**
   * Effective mass seen along `n` by a contact at offset `r` — the denominator
   * of the standard impulse solve, `1/m + n·((I⁻¹(r×n))×r)`.
   */
  inverseEffectiveMass(r: Vector3, n: Vector3): number {
    _rxn.copy(r).cross(n)
    this.applyInverseInertia(_rxn, _tmp)
    _tmp.cross(r)
    return 1 / this.mass + n.dot(_tmp)
  }

  integrate(dt: number): void {
    // --- linear ---
    this.velocity.addScaledVector(this.force, dt / this.mass)
    this.position.addScaledVector(this.velocity, dt)

    // --- angular, in the body frame where I is diagonal ---
    _qInv.copy(this.orientation).invert()
    _wBody.copy(this.omega).applyQuaternion(_qInv)
    _tBody.copy(this.torque).applyQuaternion(_qInv)

    // Euler's equations: I·ω̇ = τ − ω × (I·ω)
    _Iw.set(this.inertia.x * _wBody.x, this.inertia.y * _wBody.y, this.inertia.z * _wBody.z)
    _gyro.copy(_wBody).cross(_Iw)
    _dw.copy(_tBody).sub(_gyro).divide(this.inertia)
    _wBody.addScaledVector(_dw, dt)

    this.omega.copy(_wBody).applyQuaternion(this.orientation)

    // q̇ = ½·ω̂·q  (ω in world frame pre-multiplies)
    _spin.set(this.omega.x, this.omega.y, this.omega.z, 0)
    _spin.multiply(this.orientation)
    const q = this.orientation
    q.set(
      q.x + 0.5 * _spin.x * dt,
      q.y + 0.5 * _spin.y * dt,
      q.z + 0.5 * _spin.z * dt,
      q.w + 0.5 * _spin.w * dt,
    )
    q.normalize()

    this.force.set(0, 0, 0)
    this.torque.set(0, 0, 0)
  }

  /**
   * Inertia of a solid cylinder of height `h`, radius `r`, aligned with the
   * body's +Y axis. Yaw comes out ~20x smaller than pitch/roll, which is why
   * spinning about your own axis is easy and acrobatics are cheap (§2).
   */
  setCylinderInertia(mass: number, height: number, radius: number, scale = 1): void {
    this.mass = mass
    const pitchRoll = (mass * (3 * radius * radius + height * height)) / 12
    const yaw = (mass * radius * radius) / 2
    this.inertia.set(pitchRoll * scale, yaw * scale, pitchRoll * scale)
  }

  reset(position: Vector3, orientation?: Quaternion): void {
    this.position.copy(position)
    this.velocity.set(0, 0, 0)
    this.omega.set(0, 0, 0)
    this.orientation.copy(orientation ?? new Quaternion())
    this.force.set(0, 0, 0)
    this.torque.set(0, 0, 0)
  }
}
